//#region types
import { SupportFormTypes } from "models/formTypes";
import { AutoCompleteExtraProps, GenericFormSection } from "models/propTypes/genericFormProps";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { FormEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
//#endregion
//#region components
import { useCompanyService, useSupportService } from "serviceHooks";
import { useCheckRelations } from "components/common";
//#endregion
import { SupportService } from "services";
import { FilterHelper, QueryStateHelper } from "utils/helpers";

function useSupportIssueFilterDefinition() {
  const supportService = useSupportService();
  const companyService = useCompanyService();
  const { getIsAdmin } = useCheckRelations();
  const isAdmin = getIsAdmin();

  const apiToReportFilterFormatter = async (
    filter: WintSuperkollAPIModel.SupportIssueFilter
  ): Promise<SupportFormTypes.Filter> => {
    const reportFilter: SupportFormTypes.Filter = {
      userId: filter.userIds?.length ? filter.userIds[0] : undefined,
      userName: filter.userIds?.length ? await companyService.getEmployeeName(filter.userIds[0]) : undefined,
      searchString: filter.searchString,
      states: filter.states?.map((s) => ({ id: s, value: SupportService.getStateText(s) })),
    };

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.Support.Filter.Created,
      filter.createdDateFrom,
      filter.createdDateTo
    );

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.Support.Filter.Updated,
      filter.lastUpdatedFrom,
      filter.lastUpdatedTo
    );

    return reportFilter;
  };

  const filterFormFields: GenericFormSection<SupportFormTypes.Filter>[] = [
    {
      label: TranslationKeys.Common.Date,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        created: {
          label: TranslationKeys.Common.Created,
          type: FormEnums.FormFieldType.DATERANGE,
        },
        updated: {
          label: TranslationKeys.Common.LastUpdated,
          type: FormEnums.FormFieldType.DATERANGE,
        },
      },
    },
    {
      label: TranslationKeys.Common.General,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        userName: {
          controlLabel: TranslationKeys.SupportIssue.User,
          label: TranslationKeys.SupportIssue.User,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          icon: IconDefinitionConstants.WUser,
          hidden: !isAdmin,
          extraProps: {
            items: (searchStr: string) => companyService.SearchPersonsAutoComplete(searchStr, false),
            referenceFieldName: FormFieldConstants.Support.Filter.UserId,
          } as AutoCompleteExtraProps<SupportFormTypes.Filter>,
        },
        states: {
          label: TranslationKeys.Common.Status,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: supportService.getStates(),
          },
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<SupportFormTypes.Filter> = {
    userId: QueryStateHelper.numberQueryEncoder(FormFieldConstants.Support.Filter.UserId),
    userName: QueryStateHelper.stringQueryEncoder(FormFieldConstants.Support.Filter.UserName),
    searchString: QueryStateHelper.stringQueryEncoder(FormFieldConstants.Support.Filter.SearchString),
    created: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.Support.Filter.Created),
    updated: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.Support.Filter.Updated),
    states: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.Support.Filter.States,
      supportService.getStates()
    ),
  };

  return { filterFormFields, filterQueryStateHandler, apiToReportFilterFormatter };
}

export default useSupportIssueFilterDefinition;
